// Single source of truth for CMS landing hero / highlight banner media.
// Used by admin live preview and the public storefront so both sides agree
// (XT-432: leftover still / stale url must not diverge admin vs public).

export type CmsHeroMediaType = 'image' | 'video';

export type CmsHeroMediaFields = {
  mediaType?: CmsHeroMediaType | string | null;
  imageUrl?: string | null;
  imageKey?: string | null;
  videoUrl?: string | null;
  videoKey?: string | null;
};

export type ResolvedCmsHeroMedia = {
  /**
   * Still URL for pure-image heroes, or optional poster when a video is
   * active (leftover still). Never rendered as a full underlay while video
   * is set — see Hero / VideoCrossfade.
   */
  image: string;
  /** Resolved video URL, or undefined when the hero is image-only. */
  video: string | undefined;
};

export type CmsMediaKeyToUrl = (key: string) => string;

const nonEmpty = (value: string | null | undefined): string | undefined => {
  if (value == null) return undefined;
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : undefined;
};

/**
 * Resolve a stored media ref. **Key wins when present** so a fresh UploadThing
 * key is not masked by a stale full URL (the XT-432 failure class). External
 * YouTube/Vimeo rows only set url (key is null) and still resolve correctly.
 */
export const resolveCmsMediaUrl = (
  url: string | null | undefined,
  key: string | null | undefined,
  keyToUrl: CmsMediaKeyToUrl
): string => {
  const k = nonEmpty(key);
  if (k) return keyToUrl(k);
  return nonEmpty(url) ?? '';
};

/**
 * Resolve hero/highlight banner media.
 * - mediaType === 'video' → video active; `image` kept only as optional poster
 * - mediaType === 'image' → image only
 * - mediaType missing + video present → video wins (legacy rows)
 * - else image
 */
export function resolveCmsHeroMedia(
  fields: CmsHeroMediaFields,
  keyToUrl: CmsMediaKeyToUrl
): ResolvedCmsHeroMedia {
  const image = resolveCmsMediaUrl(fields.imageUrl, fields.imageKey, keyToUrl);
  const videoRaw = resolveCmsMediaUrl(
    fields.videoUrl,
    fields.videoKey,
    keyToUrl
  );
  const video = videoRaw || undefined;

  if (fields.mediaType === 'video') {
    // Keep leftover still as poster candidate; callers must not paint it as
    // a permanent underlay while `video` is set.
    return { image, video };
  }

  if (fields.mediaType === 'image') {
    return { image, video: undefined };
  }

  // Legacy / unset mediaType: video wins when present.
  if (video) {
    return { image, video };
  }

  return { image, video: undefined };
}

/**
 * True when the fields carry any resolvable media (non-empty key or url).
 * Used so a highlight tab only falls back to the country hero when it has
 * no media of its own — never inherits the hero's mediaType alone.
 */
export function cmsHeroFieldsHaveMedia(fields: CmsHeroMediaFields): boolean {
  return !!(
    nonEmpty(fields.imageKey) ||
    nonEmpty(fields.imageUrl) ||
    nonEmpty(fields.videoKey) ||
    nonEmpty(fields.videoUrl)
  );
}

/**
 * Prefer the section's own media (including its mediaType). Fall back to the
 * header/hero only when the section has no media fields at all.
 */
export function pickCmsHeroMediaFields(
  section: CmsHeroMediaFields | null | undefined,
  header: CmsHeroMediaFields
): CmsHeroMediaFields {
  if (section && cmsHeroFieldsHaveMedia(section)) {
    return {
      mediaType: section.mediaType,
      imageUrl: nonEmpty(section.imageUrl) ?? null,
      imageKey: nonEmpty(section.imageKey) ?? null,
      videoUrl: nonEmpty(section.videoUrl) ?? null,
      videoKey: nonEmpty(section.videoKey) ?? null,
    };
  }
  return header;
}
